import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import type { PropsWithChildren } from "react";
import { Link as RouterLink } from "react-router-dom";
import { LEGAL_REVIEW_NOTICE, PUBLIC_LEGAL_DRAFT_UPDATED } from "@bw/shared";

export function LegalDocumentLayout({
  children,
  title,
}: PropsWithChildren<{ title: string }>) {
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <AppBar
        color="default"
        elevation={0}
        position="sticky"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Toolbar disableGutters>
          <Container maxWidth="md">
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <Button
                aria-label="Go back"
                color="inherit"
                component={RouterLink}
                startIcon={<ArrowBackIcon />}
                to="/sign-in"
              >
                Back
              </Button>
              <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
                {title}
              </Typography>
            </Stack>
          </Container>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
        <Paper
          sx={{
            p: { xs: 2.5, sm: 5 },
            "& a": {
              color: "primary.main",
              textDecoration: "underline",
              textUnderlineOffset: 2,
            },
            "& h2": {
              color: "text.primary",
              fontSize: { xs: "1.35rem", sm: "1.5rem" },
              fontWeight: 700,
              lineHeight: 1.3,
              mb: 1,
              mt: 4,
            },
            "& h3": {
              color: "text.primary",
              fontSize: "1rem",
              fontWeight: 700,
              lineHeight: 1.5,
              mb: 0.75,
              mt: 2.5,
            },
            "& li": { pl: 0.5 },
            "& li + li": { mt: 0.75 },
            "& p": {
              color: "text.secondary",
              fontSize: "1rem",
              lineHeight: 1.7,
              my: 1.5,
            },
            "& strong": { color: "text.primary" },
            "& ul": {
              color: "text.secondary",
              fontSize: "1rem",
              lineHeight: 1.7,
              my: 1.5,
              pl: 3.5,
            },
          }}
          variant="outlined"
        >
          <Typography color="text.secondary" variant="body2">
            Last updated: {PUBLIC_LEGAL_DRAFT_UPDATED}
          </Typography>
          <Alert severity="warning" sx={{ my: 2 }}>
            {LEGAL_REVIEW_NOTICE}
          </Alert>
          {children}
        </Paper>
      </Container>
    </Box>
  );
}
